import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Grid, Card, Button, Avatar } from '@mui/material';
import { WaterDrop, Phone } from '@mui/icons-material';
import Layout from '../components/Layout';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const IrrigationSystems = () => {
  return (
    <Layout>
      <Helmet>
        <title>Irrigation Systems Vancouver BC | Drip Irrigation West Vancouver | Sprinkler Installation</title>
        <meta name="description" content="Professional irrigation systems Vancouver BC. Drip irrigation, sprinkler installation in West Vancouver & North Vancouver. Smart controllers. Free quotes (604) 202-0053" />
        <meta name="keywords" content="irrigation systems vancouver, drip irrigation west vancouver, sprinkler installation north vancouver, smart irrigation controllers bc, lawn sprinklers vancouver" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca/irrigation-systems" />
      </Helmet>
      
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/7.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.8) 0%, rgba(46, 125, 50, 0.7) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ ...glassStyle, p: { xs: 3, md: 6 }, color: 'white', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <WaterDrop sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h1" component="h1" sx={{ mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Professional Irrigation Systems Vancouver BC
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Efficient watering solutions for thriving landscapes
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" href="tel:+16042020053" startIcon={<Phone />} sx={{ bgcolor: 'white', color: 'primary.main' }}>
                (604) 202-0053
              </Button>
              <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
                Get Free Quote
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 4, color: 'primary.main' }}>
          Professional Irrigation Systems Vancouver BC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
          Green Mountain Landscaping specializes in <strong>irrigation systems in Vancouver</strong>, providing efficient watering solutions for West Vancouver and North Vancouver properties. Our drip irrigation and sprinkler systems keep your landscape thriving while conserving water.
        </Typography>

        <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
          Complete Irrigation Solutions Vancouver
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            { title: 'Drip Irrigation Systems Vancouver', desc: 'Water-efficient targeted watering for gardens' },
            { title: 'Sprinkler Installation BC', desc: 'Automated lawn watering systems' },
            { title: 'Smart Irrigation Controllers', desc: 'Weather-based intelligent watering control' },
            { title: 'Micro-Spray Systems', desc: 'Gentle watering for delicate plants' },
            { title: 'Irrigation Repair & Maintenance', desc: 'Keep your system running efficiently' },
            { title: 'Water-Efficient Design', desc: 'Sustainable irrigation solutions' }
          ].map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ 
                ...glassStyle, 
                p: 3, 
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                    <WaterDrop />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{service.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{service.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h3" component="h3" sx={{ mb: 3, color: 'primary.main' }}>
          How Much Does Irrigation System Cost in Vancouver?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our <strong>drip irrigation systems Vancouver</strong> are designed for maximum efficiency and cost-effectiveness. We provide customized irrigation solutions for West Vancouver properties, ensuring optimal water distribution while reducing water waste and utility costs.
        </Typography>

        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'primary.main', borderRadius: 4, color: 'white' }}>
          <Typography variant="h4" sx={{ mb: 2 }}>Ready for Smart Irrigation?</Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>Save water and keep your landscape thriving</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" href="tel:+16042020053" sx={{ bgcolor: 'white', color: 'primary.main' }}>
              Call (604) 202-0053
            </Button>
            <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
              Get Free Quote
            </Button>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default IrrigationSystems;